import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import React, { useRef, useState, useEffect, createElement } from "react";
import { cx } from "@/styled-system/css";
import { Box, Carousel, IconButton } from "@zillow/constellation-10";
import { IconChevronLeftFilled, IconChevronRightFilled } from "@zillow/constellation-icons";
import { debounce } from "lodash";
import PostCard from "../PostCard/PostCard.esm.js";
import { postsLayoutMosaicFeaturedLead, postsLayoutMosaicFeaturedFullWidth, postsLayoutGridCellSpan6, postsLayoutGridCellSpan4, postsLayoutCarouselMobileSlideWidth, postsLayoutCarouselWrapper, postsLayoutCarouselBleed } from "./posts-layout.styles.esm.js";
const CustomCarousel = ({
  children,
  ...props
}) => /* @__PURE__ */ jsx(Box, { className: postsLayoutCarouselWrapper, children: /* @__PURE__ */ jsx(Carousel, { className: postsLayoutCarouselBleed, ...props, children }) });
function getViewPort(view) {
  if (view.innerWidth < 768) {
    return "mobile";
  }
  if (view.innerWidth < 1024) {
    return "tablet";
  }
  return "desktop";
}
const PostsLayout = ({
  layout,
  items = []
}) => {
  const ref = useRef(null);
  const [currentViewPort, setCurrentViewPort] = useState("desktop");
  const layouts = {
    GRID: {
      xlDown: {
        display: "block"
      }
    },
    MOSAIC: {
      // Below xl: column stack (not 12-col grid) so lead card can go full width. Use flex+`gap` — `display:block` drops `gap` from the root grid, so lead + carousel touched with no vertical space.
      xlDown: {
        display: "flex",
        flexDirection: "column",
        gap: "{spacing.loose}"
      }
    },
    INLINE: {
      display: items.length > 3 ? "block" : "grid",
      xlDown: {
        display: "block"
      }
    },
    CAROUSEL: {
      display: "block"
    }
  };
  useEffect(() => {
    const {
      ownerDocument
    } = ref.current;
    const {
      defaultView
    } = ownerDocument;
    const view = defaultView;
    const onResize = () => {
      setCurrentViewPort(getViewPort(view));
    };
    const debouncedOnResize = debounce(onResize, 100);
    setCurrentViewPort(getViewPort(view));
    view.addEventListener("resize", debouncedOnResize);
    view.addEventListener("orientationchange", debouncedOnResize);
    return () => {
      view.removeEventListener("resize", debouncedOnResize);
      view.removeEventListener("orientationchange", debouncedOnResize);
    };
  }, []);
  const renderControls = (leftButtonProps, rightButtonProps) => {
    if (currentViewPort !== "mobile") {
      return /* @__PURE__ */ jsxs(Box, { css: {
        position: "absolute",
        top: "100%",
        right: "0",
        display: "grid",
        gridAutoFlow: "column",
        columnGap: "{spacing.default}",
        alignItems: "start"
      }, children: [
        /* @__PURE__ */ jsx(IconButton, { "aria-hidden": true, shape: "circle", "aria-label": "Previous", emphasis: "outlined", tone: "neutral", icon: /* @__PURE__ */ jsx(IconChevronLeftFilled, {}), size: "sm", tabIndex: -1, title: "Previous items", ...leftButtonProps }),
        /* @__PURE__ */ jsx(IconButton, { "aria-hidden": true, shape: "circle", "aria-label": "Next", emphasis: "outlined", tone: "neutral", icon: /* @__PURE__ */ jsx(IconChevronRightFilled, {}), size: "sm", tabIndex: -1, title: "Next items", ...rightButtonProps })
      ] });
    }
    return null;
  };
  let PostsItems = () => /* @__PURE__ */ jsx(Fragment, { children: items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id })) });
  if (layout === "MOSAIC") {
    PostsItems = function PostItems() {
      var _a, _b, _c;
      const mosaicLeadStyle = currentViewPort === "desktop" && items.length > 1 ? {
        ...(_a = items[0]) == null ? void 0 : _a.style,
        gridRowEnd: `span ${items.length - 1}`
      } : (_b = items[0]) == null ? void 0 : _b.style;
      return /* @__PURE__ */ jsxs(React.Fragment, { children: [
        /* @__PURE__ */ jsx(PostCard, { ...items[0], alignment: "vertical", size: "lg", className: cx(currentViewPort === "desktop" ? postsLayoutMosaicFeaturedLead : postsLayoutMosaicFeaturedFullWidth, (_c = items[0]) == null ? void 0 : _c.className), style: mosaicLeadStyle }),
        currentViewPort === "mobile" || currentViewPort === "tablet" ? /* @__PURE__ */ jsx(CustomCarousel, { renderNavigationControls: renderControls, slideWidth: "320px", enableSnap: true, slideAlign: "start", scrollOffset: "sm", children: items.slice(1).map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "vertical", size: "md", excerpt: void 0 })) }) : items.slice(1).map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "horizontal", size: "md", excerpt: void 0, className: cx(postsLayoutGridCellSpan6, item.className) }))
      ] });
    };
  }
  if (layout === "INLINE") {
    PostsItems = function PostItems() {
      return currentViewPort === "mobile" || currentViewPort === "tablet" || items.length > 3 ? /* @__PURE__ */ jsx(CustomCarousel, { renderNavigationControls: renderControls, slideWidth: "320px", enableSnap: true, slideAlign: "start", scrollOffset: "sm", children: items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "vertical", size: "md", excerpt: void 0 })) }) : /* @__PURE__ */ jsx(Fragment, { children: items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "vertical", size: "md", excerpt: void 0, className: cx(postsLayoutGridCellSpan4, item.className) })) });
    };
  }
  if (layout === "CAROUSEL") {
    PostsItems = function PostsItemsCarousel() {
      if (items.length === 0) return null;
      if (items.length <= 1) {
        return /* @__PURE__ */ createElement(PostCard, { ...items[0], key: items[0].id, alignment: "horizontal", size: "xl" });
      }
      const slideWidth = currentViewPort === "mobile" ? postsLayoutCarouselMobileSlideWidth : "100%";
      return /* @__PURE__ */ jsx(CustomCarousel, { renderNavigationControls: renderControls, slideWidth, enableSnap: true, slideAlign: "start", scrollOffset: "sm", children: items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "horizontal", size: "xl" })) });
    };
  }
  if (layout === "GRID") {
    PostsItems = function PostsItemsGrid() {
      return currentViewPort === "mobile" || currentViewPort === "tablet" ? /* @__PURE__ */ jsx(CustomCarousel, { renderNavigationControls: renderControls, slideWidth: "320px", enableSnap: true, slideAlign: "start", scrollOffset: "sm", children: items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "vertical", size: "md", excerpt: void 0 })) }) : /* @__PURE__ */ jsx(Fragment, { children: items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "horizontal", size: "md", excerpt: void 0, className: cx(postsLayoutGridCellSpan6, item.className) })) });
    };
  }
  return /* @__PURE__ */ jsx(Box, { ref, css: {
    display: "grid",
    gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
    gap: "{spacing.loose}",
    ...layouts[layout]
  }, children: /* @__PURE__ */ jsx(PostsItems, {}) });
};
export {
  PostsLayout as default
};
